// A unit's tasks (activities): the pure rules shared by today's Activity tab
// (UnitDetailPane/ActivityTab) and the full unit page's Activity tab
// (UnitV2/tabs/ActivityTabV2), so the two cannot drift.
import { isOverdue, toDueDatePayload } from './taskBoard';

// Today's type labels and colours (v1) plus the v2 token tone for each.
export const ACTIVITY_TYPES = Object.freeze({
    price_change: { label: 'Price Change', color: '#1d4ed8', bg: '#1d4ed815', tone: 'info' },
    photo_update: { label: 'Photo Update', color: '#b45309', bg: '#b4530915', tone: 'warn' },
    description: { label: 'Description', color: '#0f766e', bg: '#0f766e15', tone: 'teal' },
    other: { label: 'Other', color: '#64748b', bg: '#64748b15', tone: 'neutral' },
});

export const ACTIVITY_STATUSES = Object.freeze({
    open: { label: 'Open', color: '#b45309', bg: '#fffbeb', border: '#fcd34d', tone: 'open' },
    in_progress: { label: 'In Progress', color: '#1d4ed8', bg: '#eff6ff', border: '#93c5fd', tone: 'progress' },
    completed: { label: 'Completed', color: '#15803d', bg: '#f0fdf4', border: '#86efac', tone: 'done' },
    cancelled: { label: 'Cancelled', color: '#64748b', bg: '#f8fafc', border: '#e2e8f0', tone: 'cancelled' },
});

export const activityType = (type) => ACTIVITY_TYPES[type] || ACTIVITY_TYPES.other;
export const activityStatus = (status) => ACTIVITY_STATUSES[status] || ACTIVITY_STATUSES.open;

// The status filter buttons, in today's order and wording.
export const ACTIVITY_FILTERS = Object.freeze(['all', 'open', 'in_progress', 'completed', 'cancelled']);
export const filterLabel = (f) => {
    if (f === 'all') return 'All';
    if (f === 'in_progress') return 'In Progress';
    return f.charAt(0).toUpperCase() + f.slice(1);
};
export const filterActivities = (activities, filter) => (
    filter === 'all' ? activities : activities.filter((a) => a.status === filter)
);
export const emptyActivitiesText = (filter) => (
    filter === 'all' ? 'No activities yet for this unit.' : `No ${filter} activities.`
);

export const isOpenActivity = (a) => a.status === 'open' || a.status === 'in_progress';
export const isClosedActivity = (a) => a.status === 'completed' || a.status === 'cancelled';
// A due date is a calendar day: late from the day after.
export const isActivityOverdue = (a, now) => isOverdue(a, now);

export const AI_DESCRIPTION_SEPARATOR = '--- AI-Generated Description ---';

/** Details split into the rationale and the AI description (null when none). */
export function splitDetails(details) {
    if (!details) return { rationale: '', aiDescription: null };
    if (!details.includes(AI_DESCRIPTION_SEPARATOR)) return { rationale: details, aiDescription: null };
    const [rationale, aiDesc] = details.split(AI_DESCRIPTION_SEPARATOR);
    return { rationale: rationale.trim(), aiDescription: (aiDesc || '').trim() };
}

/** POST /api/activities body for the create form. */
export function buildCreatePayload(unit, {
    type, title, details, suggestedPrice, assignedTo, dueDate,
}) {
    return {
        stock_number: unit.StockNumber,
        type,
        title: title.trim(),
        details,
        suggested_price: suggestedPrice ? Number(suggestedPrice) : null,
        current_price: unit.Best_Sell_Price || null,
        assigned_to: assignedTo || null,
        due_date: toDueDatePayload(dueDate),
    };
}

/** The fields a status change applies: the completion stamp belongs to "completed" only. */
export const statusChangeFields = (newStatus) => (
    newStatus === 'completed' ? { status: newStatus } : { status: newStatus, completed_at: null, completed_by: null }
);

export const fmtActivityDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
